/**
 * ProfileSettings - Manage public/private profile visibility
 */
import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Globe,
  Lock,
  User,
  Link2,
  Copy,
  Check,
  Loader2,
  Eye,
  Trophy,
  MapPin,
  BarChart3,
  ExternalLink,
  Palette,
} from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

import { BACKEND_URL as API_URL } from "@/config/api";
const APP_URL = "https://duolyst.co.uk";

export const ProfileSettings = () => {
  const [settings, setSettings] = useState({
    is_public: false,
    username: "",
    bio: "",
    show_stats: true,
    show_badges: true,
    show_countries: true,
    use_rank_theme: true,
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [copied, setCopied] = useState(false);
  const [usernameAvailable, setUsernameAvailable] = useState(null);
  const [checkingUsername, setCheckingUsername] = useState(false);

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/profile/settings`);
      setSettings(response.data);
    } catch (error) {
      console.error("Failed to fetch profile settings:", error);
    } finally {
      setLoading(false);
    }
  };

  const checkUsername = async (username) => {
    if (!username || username.length < 3) {
      setUsernameAvailable(null);
      return;
    }

    setCheckingUsername(true);
    try {
      const response = await axios.get(
        `${API_URL}/api/profile/check-username/${username}`
      );
      setUsernameAvailable(response.data.available);
    } catch (error) {
      console.error("Username check failed:", error.message);
      setUsernameAvailable(null);
    } finally {
      setCheckingUsername(false);
    }
  };

  const handleUsernameChange = (value) => {
    const cleaned = value.toLowerCase().replace(/[^a-z0-9_-]/g, "");
    setSettings({ ...settings, username: cleaned });
    
    // Debounce username check
    clearTimeout(window.usernameCheckTimeout);
    window.usernameCheckTimeout = setTimeout(() => {
      checkUsername(cleaned);
    }, 500);
  };

  const handleSave = async () => {
    if (settings.is_public && !settings.username) {
      toast.error("Please set a username to make your profile public");
      return;
    }

    if (settings.username && settings.username.length < 3) {
      toast.error("Username must be at least 3 characters");
      return;
    }

    setSaving(true);
    try {
      await axios.put(`${API_URL}/api/profile/settings`, settings);
      toast.success("Profile settings saved");
      fetchSettings();
      // Trigger immediate re-fetch so RankThemeProvider picks up new preference
      window.dispatchEvent(new Event("duolyst:user-updated"));
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save settings");
    } finally {
      setSaving(false);
    }
  };

  const copyProfileLink = () => {
    const link = `${APP_URL}/u/${settings.username}`;
    navigator.clipboard.writeText(link);
    setCopied(true);
    toast.success("Profile link copied!");
    setTimeout(() => setCopied(false), 2000);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="w-6 h-6 animate-spin text-primary" />
      </div>
    );
  }

  const profileUrl = settings.username ? `${APP_URL}/u/${settings.username}` : null;

  return (
    <div className="space-y-6">
      {/* Public/Private Toggle */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              {settings.is_public ? (
                <div className="p-2 bg-green-100 rounded-lg">
                  <Globe className="w-5 h-5 text-green-600" />
                </div>
              ) : (
                <div className="p-2 bg-charcoal/10 rounded-lg">
                  <Lock className="w-5 h-5 text-charcoal" />
                </div>
              )}
              <div>
                <CardTitle className="text-lg">Profile Visibility</CardTitle>
                <CardDescription>
                  {settings.is_public
                    ? "Your profile is visible to everyone"
                    : "Your profile is private"}
                </CardDescription>
              </div>
            </div>
            <Switch
              checked={settings.is_public}
              onCheckedChange={(checked) =>
                setSettings({ ...settings, is_public: checked })
              }
            />
          </div>
        </CardHeader>
      </Card>

      {/* Profile URL */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <Link2 className="w-5 h-5" />
            Profile URL
          </CardTitle>
          <CardDescription>
            Choose a unique username for your shareable profile
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="username">Username</Label>
            <div className="flex gap-2">
              <div className="relative flex-1">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">
                  @
                </span>
                <Input
                  id="username"
                  value={settings.username}
                  onChange={(e) => handleUsernameChange(e.target.value)}
                  placeholder="yourname"
                  className="pl-8"
                  maxLength={30}
                />
                {checkingUsername && (
                  <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 animate-spin text-muted-foreground" />
                )}
                {!checkingUsername && usernameAvailable === true && (
                  <Check className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-green-500" />
                )}
                {!checkingUsername && usernameAvailable === false && (
                  <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-red-500">
                    Taken
                  </span>
                )}
              </div>
            </div>
            <p className="text-xs text-muted-foreground">
              Letters, numbers, underscores, and hyphens only (3-30 characters)
            </p>
          </div>

          {profileUrl && settings.is_public && (
            <div className="flex items-center gap-2 p-3 bg-muted rounded-lg">
              <Globe className="w-4 h-4 text-muted-foreground shrink-0" />
              <span className="text-sm text-charcoal truncate flex-1">
                {profileUrl}
              </span>
              <Button variant="ghost" size="sm" onClick={copyProfileLink}>
                {copied ? (
                  <Check className="w-4 h-4 text-green-500" />
                ) : (
                  <Copy className="w-4 h-4" />
                )}
              </Button>
              <a
                href={`/u/${settings.username}`}
                target="_blank"
                rel="noopener noreferrer"
              >
                <Button variant="ghost" size="sm">
                  <ExternalLink className="w-4 h-4" />
                </Button>
              </a>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Bio */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <User className="w-5 h-5" />
            About You
          </CardTitle>
          <CardDescription>
            A short bio shown on your public profile
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Textarea
            value={settings.bio || ""}
            onChange={(e) => setSettings({ ...settings, bio: e.target.value })}
            placeholder="Tell others about your travel style..."
            className="resize-none"
            rows={3}
            maxLength={200}
          />
          <p className="text-xs text-muted-foreground mt-1 text-right">
            {(settings.bio || "").length}/200
          </p>
        </CardContent>
      </Card>

      {/* Visibility Options */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <Eye className="w-5 h-5" />
            What to Show
          </CardTitle>
          <CardDescription>
            Choose what visitors can see on your profile
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <BarChart3 className="w-5 h-5 text-muted-foreground" />
              <div>
                <p className="font-medium text-sm">Travel Stats</p>
                <p className="text-xs text-muted-foreground">
                  Completed adventures, countries visited
                </p>
              </div>
            </div>
            <Switch
              checked={settings.show_stats}
              onCheckedChange={(checked) =>
                setSettings({ ...settings, show_stats: checked })
              }
            />
          </div>

          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Trophy className="w-5 h-5 text-muted-foreground" />
              <div>
                <p className="font-medium text-sm">Achievement Badges</p>
                <p className="text-xs text-muted-foreground">
                  Badges you've earned
                </p>
              </div>
            </div>
            <Switch
              checked={settings.show_badges}
              onCheckedChange={(checked) =>
                setSettings({ ...settings, show_badges: checked })
              }
            />
          </div>

          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <MapPin className="w-5 h-5 text-muted-foreground" />
              <div>
                <p className="font-medium text-sm">Countries Visited</p>
                <p className="text-xs text-muted-foreground">
                  List of countries you've explored
                </p>
              </div>
            </div>
            <Switch
              checked={settings.show_countries}
              onCheckedChange={(checked) =>
                setSettings({ ...settings, show_countries: checked })
              }
            />
          </div>
        </CardContent>
      </Card>

      {/* Appearance */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <Palette className="w-5 h-5" />
            Appearance
          </CardTitle>
          <CardDescription>
            Personalise how DuoLyst looks for you
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-5 h-5 rounded-full bg-primary" />
              <div>
                <p className="font-medium text-sm">Travel-rank colour</p>
                <p className="text-xs text-muted-foreground">
                  Tints buttons, tabs and outlines to match your rank
                </p>
              </div>
            </div>
            <Switch
              checked={settings.use_rank_theme}
              onCheckedChange={(checked) => {
                setSettings({ ...settings, use_rank_theme: checked });
              }}
              data-testid="settings-rank-theme-toggle"
            />
          </div>
        </CardContent>
      </Card>

      {/* Save Button */}
      <Button
        onClick={handleSave}
        disabled={saving}
        className="w-full bg-primary hover:bg-primary/90"
      >
        {saving ? (
          <>
            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            Saving...
          </>
        ) : (
          "Save Changes"
        )}
      </Button>
    </div>
  );
};

export default ProfileSettings;
